import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import FloatingShapes from "../components/FloatingShapes";
import { api } from "../lib/site";
import { IMG } from "../lib/images";

const WORKER_TYPES = ["Light Industrial / Warehouse", "Manufacturing / Production", "Administrative / Clerical", "Skilled Trades", "Direct Hire", "Other"];
const DURATIONS = ["Short-term (1-4 weeks)", "Seasonal / Peak", "Temp-to-Hire", "Long-term / Ongoing", "Permanent / Direct Hire"];

export default function Employers() {
  const [form, setForm] = useState({ first_name: "", last_name: "", company_name: "", worker_type: WORKER_TYPES[0], duration: DURATIONS[0], email: "", phone: "", message: "", company_url: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [who, setWho] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.company_name) {
      toast.error("Please fill in your name and company.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/employer-requests", { ...form, is_employer: who === "employer" });
      setDone(true);
      toast.success("Request received — we'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="employers-page" className="pt-[72px] min-h-screen flex items-center">
        <div className="mx-auto max-w-[1500px] w-full px-6 md:px-10 py-32">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <CheckCircle2 size={48} className="text-cobalt mb-8" />
            <h1 className="font-display uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl text-cobalt">Request received.</h1>
            <p className="text-muted-foreground text-lg mt-6 max-w-xl">Thank you, {form.first_name}. Our team at HR Direct Services will review your request and reach out to {form.company_name} shortly. We'll handle it from here.</p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="employers-page" className="relative pt-[72px] overflow-hidden">
      <FloatingShapes />
      <section className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-10 pt-20 md:pt-28 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <p className="overline text-flame mb-6">Employers</p>
          <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[12vw] lg:text-[7vw]">
            <MaskedLines lines={["Tell us what", "you need. We'll", <span key="3" className="text-cobalt">handle it.</span>]} delay={0.2} />
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="text-muted-foreground text-lg">Leave a few details and our staffing team takes it from there — sourcing, screening and deploying the right workers for your operation.</p>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-10 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <div data-testid="employer-only-banner" className="flex items-start gap-3 rounded-2xl border border-flame/40 bg-flame/10 p-5 mb-10">
            <AlertTriangle size={20} className="text-flame shrink-0 mt-0.5" />
            <p className="text-sm">
              <strong>This form is for employers only</strong> — businesses looking to hire workers. Looking for a job? Do not use this form.{" "}
              <Link to="/apply" data-testid="banner-apply-link" className="text-cobalt underline font-semibold">Apply now</Link> or{" "}
              <Link to="/jobs" data-testid="banner-jobs-link" className="text-cobalt underline font-semibold">see jobs available now</Link>.
            </p>
          </div>
          <div className="mb-10">
            <label className="overline text-muted-foreground">Which describes you?<span className="text-flame"> *</span></label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {[["employer", "I'm an employer looking to hire workers"], ["seeker", "I'm looking for a job"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`emp-who-${v}`} onClick={() => setWho(v)} className={`text-left rounded-xl border px-5 py-4 transition-colors duration-200 ${who === v ? "border-cobalt bg-cobalt text-white" : "border-border hover:border-cobalt"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {who === "seeker" && (
            <motion.div data-testid="seeker-redirect" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-ink text-bone p-8">
              <p className="font-display uppercase tracking-tight text-3xl">You're in the wrong place — but we can help.</p>
              <p className="text-bone/80 mt-3">Job seekers apply through our application or job board. Your info won't reach our hiring team from this employer form.</p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to="/apply" data-testid="seeker-apply-btn" className="inline-flex items-center gap-2 bg-cobalt text-white px-6 py-3 rounded-full overline hover:bg-flame transition-colors duration-300">Apply now <ArrowUpRight size={14} /></Link>
                <Link to="/jobs" data-testid="seeker-jobs-btn" className="inline-flex items-center gap-2 border border-bone/40 px-6 py-3 rounded-full overline hover:bg-bone hover:text-ink transition-colors duration-300">View open jobs</Link>
              </div>
            </motion.div>
          )}
          {who === "employer" && (
          <form onSubmit={submit} data-testid="employer-form" className="space-y-10">
            <input type="text" name="company_url" value={form.company_url} onChange={set("company_url")} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="First Name" testid="emp-first-name" value={form.first_name} onChange={set("first_name")} required />
              <Field label="Last Name" testid="emp-last-name" value={form.last_name} onChange={set("last_name")} required />
            </div>
            <Field label="Company Name" testid="emp-company" value={form.company_name} onChange={set("company_name")} required />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <SelectField label="Type of worker needed" testid="emp-worker-type" value={form.worker_type} onChange={set("worker_type")} options={WORKER_TYPES} />
              <SelectField label="For how long" testid="emp-duration" value={form.duration} onChange={set("duration")} options={DURATIONS} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="Email (optional)" testid="emp-email" type="email" value={form.email} onChange={set("email")} />
              <Field label="Phone (optional)" testid="emp-phone" value={form.phone} onChange={set("phone")} />
            </div>
            <div>
              <label className="overline text-muted-foreground">Anything else? (optional)</label>
              <textarea data-testid="emp-message" rows={3} value={form.message} onChange={set("message")} className="field-line mt-2 resize-none" />
            </div>
            <button type="submit" data-testid="employer-submit" disabled={loading} className="inline-flex items-center gap-2 bg-cobalt text-white px-8 py-4 rounded-full overline hover:bg-flame transition-colors duration-300 disabled:opacity-50">
              {loading ? "Sending…" : "Send request"} <ArrowUpRight size={14} />
            </button>
          </form>
          )}
        </div>
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative h-[520px] overflow-hidden rounded-2xl">
              <img src={IMG.handshakeHire} alt="Employer partnership handshake" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-cobalt/10" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-10 pb-28 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[IMG.signing, IMG.mentoring, IMG.handshake].map((src, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="relative h-[280px] overflow-hidden rounded-2xl">
              <img src={src} alt="Working with HR Direct Services" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        ))}
      </section>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}{required && <span className="text-flame"> *</span>}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} className="field-line mt-2" />
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <select data-testid={testid} value={value} onChange={onChange} className="field-line mt-2 cursor-pointer">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
